import {DataGrid, GridColDef, GridLocaleText, GridRowId, GridRowsProp, GridSortModel, GridToolbar} from "@mui/x-data-grid";
import {ObjectWithId} from "./CrudTable";
import React from "react";
import {GridRowSelectionModel} from "@mui/x-data-grid/models/gridRowSelectionModel";


export interface TableProps<T extends object> {
    data: T[]
    setSelectedData?: (t: T | undefined) => void
    selectionModel?: GridRowId[]
    setSelectionModel?: (gridRowSelectionModel: GridRowSelectionModel) => void
    dataTestId?: string
    pageSize?: number
    disableVirtualization?: boolean
    checkboxSelection?: boolean
    customColumns?: GridColDef[]
    enableAdditionalControls?: boolean
    localeText?: Partial<GridLocaleText>
    initialSortModel?: GridSortModel
}

export function getHeaders<T extends object>(data: T[]) {
    if(data[0]) {
        const columns: GridColDef[] = Object.keys(data[0]).map((v) => {
            return {field: v, headerClassName: 'table-column-header', headerName: v, flex: 1}
        })
        return columns;
    }
    return []
}

export function toRows<T extends object>(data: T[]) {
    const rows: GridRowsProp = data.map((v, i) => {
        const row = {id: i}
        const values = Object.entries(v)
        values.map((vv) => {
            const [k,v] = vv
            row[k] = v
        })
        return row
    })
    return rows;
}

export function getSelectedData<T extends ObjectWithId>(row, columns, data: T[]): T | undefined {
    const field = columns.find(f => f.headerName == 'queryableDataId')?.field ?? ''
    return data.find(u => u.queryableDataId == row[field])
}

export function SingleTable<T extends ObjectWithId>({data, setSelectedData = () => [], selectionModel = [], setSelectionModel = () => [], dataTestId, pageSize, disableVirtualization, checkboxSelection, customColumns, enableAdditionalControls, localeText, initialSortModel}: TableProps<T>) {
    const columns = customColumns ?? getHeaders(data);
    const rows = toRows(data)
    const disableAdditionalControls = enableAdditionalControls == undefined ? false : !enableAdditionalControls;

    return (
        <div style={{width: '100%'}} data-testid={dataTestId}>
            <DataGrid
                disableVirtualization={disableVirtualization ?? false}
                rows={rows}
                columns={columns}
                columnVisibilityModel={{queryableDataId: false}}
                pageSizeOptions={[pageSize ?? 5]}
                checkboxSelection={checkboxSelection ?? true}
                hideFooterSelectedRowCount
                rowSelectionModel={selectionModel}
                disableColumnMenu={disableAdditionalControls}
                disableColumnFilter={disableAdditionalControls}
                disableColumnSelector={disableAdditionalControls}
                disableDensitySelector={disableAdditionalControls}
                disableRowSelectionOnClick={disableAdditionalControls}
                density='compact'
                onRowSelectionModelChange={(selection) => {
                    if(selection.length == 0 ){
                        setSelectionModel([])
                        setSelectedData(undefined)
                    }
                    else if (selection.length > 1) {
                        const selectionSet = new Set(selectionModel);
                        const result = selection.filter((s) => !selectionSet.has(s));
                        setSelectionModel(result);
                        setSelectedData(getSelectedData(rows[selection[1]], columns, data))
                    } else {
                        setSelectedData(getSelectedData(rows[selection[0]], columns, data))
                        setSelectionModel(selection);
                    }
                }}
                initialState={{
                    pagination: {
                        paginationModel: {
                            pageSize: pageSize??5,
                        },
                    },
                    sorting: {
                        sortModel: initialSortModel ?? [],
                    },
                }}

                localeText={localeText}
                slots={{
                    toolbar: GridToolbar,
                }}
                slotProps={{
                    toolbar: {
                        showQuickFilter: true,
                        quickFilterProps: {debounceMs: 500},
                    },
                }}
                sx={{
                    "& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer": {
                        display: "none"
                    }
                }}
             />
        </div>
    )
}
